import { motion, type Transition, type Variants } from 'framer-motion';
import { memo, useState } from 'react';
import { items } from '~/lib/data/faq';
import { HELP_CENTER_URL } from '~/lib/urls';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';

const variants = {
  closed: {
    height: 0,
    opacity: 0,
    transitionEnd: {
      visibility: 'hidden',
    },
  },
  open: {
    height: 'auto',
    opacity: 1,
    visibility: 'visible',
  },
} satisfies Variants;

const transition = {
  duration: 0.2,
  ease: cubicEasingFn,
} satisfies Transition;

const INITIAL_OPEN_INDICES = new Set<number>();

export const FAQ = memo(() => {
  const [openIndices, setOpenIndices] = useState<Set<number>>(INITIAL_OPEN_INDICES);

  const onToggleQuestion = (event: React.MouseEvent<HTMLDetailsElement>, index: number) => {
    event.preventDefault();

    setOpenIndices((prevSet) => {
      const newSet = new Set(prevSet);

      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }

      return newSet;
    });
  };

  return (
    <section className="flex flex-col justify-center items-center mt-10">
      <div className="mb-11 text-center">
        <h2 className="text-2xl font-semibold mb-3">Frequently asked questions</h2>
        <p className="text-bolt-elements-textSecondary">Everything you need to know about the product and billing.</p>
      </div>
      <div className="flex flex-col gap-4 w-3xl max-w-full">
        {items.map((item, index) => (
          <details
            key={index}
            className={classNames('px-6 py-4 rounded-lg', {
              'bg-bolt-elements-background-depth-2': openIndices.has(index),
            })}
            open={true}
            aria-expanded={openIndices.has(index)}
            onClick={(event) => onToggleQuestion(event, index)}
          >
            <summary className="block cursor-pointer list-none [&::-webkit-details-marker]:hidden">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className={classNames('text-base', openIndices.has(index) ? 'i-ph:caret-up' : 'i-ph:caret-down')}
                />
                <h3 className="text-bolt-elements-textPrimary text-base">{item.question}</h3>
              </div>
            </summary>
            <motion.div
              initial="closed"
              animate={openIndices.has(index) ? 'open' : 'closed'}
              variants={variants}
              transition={transition}
              onClick={(e) => e.stopPropagation()}
            >
              <p className="ml-6 text-bolt-elements-textSecondary text-sm/6">{item.answer}</p>
            </motion.div>
          </details>
        ))}
        <button
          onClick={() => window.open(HELP_CENTER_URL, '_blank')}
          className="mt-7 px-5 py-4 rounded-md bg-alpha-accent-10 hover:bg-alpha-accent-20 flex justify-between items-center"
        >
          <p className="text-sm text-left">
            For additional information, please visit our <span className="text-bolt-elements-link">Help Center</span>.
          </p>
          <div className="i-ph:arrow-right w-4 h-4 text-bolt-elements-link" />
        </button>
      </div>
    </section>
  );
});
